<template>
  <v-select
    :class="{
      'v-select-danger': invalid === false,
      'select-size-sm': small,
    }"

    :value="value"
    :name="name"
    :options="filteredOpts"
    :disabled="isDisabled"
    :placeholder="computedPlaceholder"
    :loading="isLoading"

    :selectable="opt => !opt.disabled"
    :reduce="opt => opt.value"
    :create-option="opt => ({ value: opt, label: opt })"
    :close-on-select="!multiple"

    :clearable="clearable"
    :taggable="taggable"
    :searchable="searchable"
    :filterable="filterable"
    :multiple="multiple"

    @option:selecting="handleSelecting"
    @input="onInput"
  >
    <template #spinner="{ loading }">
      <b-spinner
        v-if="loading"
        variant="primary"
        small
      />
    </template>
    <template #no-options>
      No hay opciones para el criterio de búsqueda
    </template>
  </v-select>
</template>

<script>
import { listOptions } from '@/febos/servicios/api/opciones.api';
import { uniqueByField } from '@/utils';

const cache = {};
const promises = {};

export default {
  name: 'select-options',
  data() {
    return {
      opts: [],
      loadingOptions: false,
    };
  },
  computed: {
    isLoading() {
      return this.loading || this.loadingOptions;
    },
    computedPlaceholder() {
      if (this.isLoading) return 'Cargando opciones...';
      if (this.opts.length === 0) return 'No hay opciones disponibles';
      if (this.placeholder) return this.placeholder;
      return this.multiple ? 'Seleccione una o más opciones' : 'Seleccione una opción';
    },
    isDisabled() {
      return this.disabled
        || !this.opts.length
        || this.loadingOptions;
    },
    filteredOpts() {
      if (!this.filterByDocType) {
        return this.opts;
      }

      return this.opts.filter(option => {
        if (!option.extra?.availableFor) {
          return true;
        }

        return option.extra.availableFor.includes(this.filterByDocType);
      });
    },
  },
  methods: {
    getOption() {
      return this.opts.find((option) => option.value === this.value);
    },
    handleSelecting(option) {
      if (this.multiple && this.value && this.value.length && this.value.includes(option.value)) {
        this.$emit('input', this.value.filter((v) => v !== option.value));
      }
    },
    async loadOptions() {
      this.loadingOptions = true;

      try {
        if (this.options && this.options.length > 0) {
          this.opts = this.options.map(this.mapper);
          return;
        }

        if (!this.optionsGroup) {
          this.loadingOptions = false;
          this.opts = [];
          return;
        }

        if (promises[this.optionsGroup]) {
          await promises[this.optionsGroup];
          this.opts = cache[this.optionsGroup];
          return;
        }

        if (cache[this.optionsGroup]) {
          this.opts = cache[this.optionsGroup];
          return;
        }

        promises[this.optionsGroup] = this.fetchOptions();
        await promises[this.optionsGroup];
      } finally {
        let option = null;

        if (this.defaultOption) {
          option = this.opts.find((opt) => opt.value === this.defaultOption);
        }

        if (this.opts.some((opt) => opt.default)) {
          option = this.opts.find((opt) => opt.default);
        }

        if (option) this.$emit('input', option.value);

        this.loadingOptions = false;
      }
    },
    async fetchOptions() {
      try {
        await this.tryFetchOptions();
      } catch (e0) {
        console.error('error fetching options (1 attempt)', e0);
        try {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          await this.tryFetchOptions();
        } catch (e1) {
          console.error('error fetching options (2 attempts)', e1);
        }
      } finally {
        delete promises[this.optionsGroup];
      }
    },
    async tryFetchOptions() {
      const { data } = await listOptions({
        grupoOpcion: this.optionsGroup,
        deshabilitado: 'no',
        orden: '+orden',
      });

      const parsedOpts = uniqueByField(data.opciones, 'valor').map((option) => {
        let extra;
        try {
          extra = JSON.parse(option.extra);
        } catch { /**/ }

        const def = extra.default || false;

        return {
          id: option.opcionId,
          value: option.valor,
          label: option.descripcion,
          order: option.orden,
          extra: extra || {},
          ...(def ? { default: true } : {}),
        };
      });

      let opts = this.extraOptions.concat(parsedOpts).map(this.mapper);

      if (this.alphabeticalSort) {
        opts = opts.sort((a, b) => a.label.localeCompare(b.label));
      }

      cache[this.optionsGroup] = opts;
      this.opts = opts;
      if (this.opts.length === 1 && this.selectFirst) {
        // this.value = this.opts[0].value;
        this.$emit('input', this.opts[0].value);
      }
    },
    onInput(event) {
      this.$emit('input', event);
    },
  },
  created() {
    this.loadOptions();
  },
  watch: {
    options() {
      this.loadOptions();
    },
    opts() {
      if (this.opts.length === 1 && this.selectFirst) {
        // this.value = this.opts[0].value;
        this.$emit('input', this.opts[0].value);
      }
    }
  },
  props: {
    name: { type: String, required: true },
    invalid: { type: Boolean, default: null },
    validator: Object,
    small: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: null },
    value: { type: [String, Array], default: null },
    loading: { type: Boolean, default: false },

    optionsGroup: String,

    taggable: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    searchable: { type: Boolean, default: true },
    filterable: { type: Boolean, default: true },
    multiple: { type: Boolean, default: false },
    alphabeticalSort: { type: Boolean, default: false },

    mapper: {
      type: Function,
      default: (opt) => ({
        value: opt.value,
        label: opt.label,
        id: opt.id,
        extra: opt.extra,
        ...(opt.default ? { default: true } : {}),
      })
    },
    extraOptions: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
    defaultOption: {
      type: [String, Object],
      default: null,
    },
    filterByDocType: {
      type: String,
      default: null,
    },
    selectFirst: {
      type: Boolean,
      default: false,
    }
  },
};
</script>

<style>
.vs__dropdown-option.vs__dropdown-option--selected.vs__dropdown-option--highlight {
  opacity: 0.95;
}
</style>
